import React, { useState, useEffect, useContext } from 'react';
import { type DynamicEntitiesRef } from '../DynamicEntities/ui/DynamicEntities';
import { EntityContainer } from '../DynamicEntities/ui/DynamicFeatureOrchestrator';
import { DynamicEntitiesRegistrationContext } from './DynamicEntitiesRegistrationContext';

export interface MasterCrudDynamicEntitiesProps {
    /** Metadatos de la característica dinámica cargada */
    dynamicFeature: any;
    /** Código de la característica dinámica */
    dynamicFeatureCode?: string;
    /** ID del registro actual (para cargar datos EAV) */
    recordId?: string | number;
    /** Función para registrar referencias de componentes dinámicos internos */
    registerDynamicRef?: (entityCode: string, ref: any) => void;
    /** Si es true, solo muestra entidades de tipo Planes y Criterios. Si es false, las excluye. */
    showOnlyPlansAndCriteria?: boolean;
}

export const MasterCrudDynamicEntities: React.FC<MasterCrudDynamicEntitiesProps> = ({
    dynamicFeature,
    dynamicFeatureCode,
    recordId,
    registerDynamicRef,
    showOnlyPlansAndCriteria,
}) => {
    const registrationContext = useContext(DynamicEntitiesRegistrationContext);
    useEffect(() => {
        registrationContext?.register();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [activeDynamicTab, setActiveDynamicTab] = useState<string>('');

    useEffect(() => {
        if (!activeDynamicTab && dynamicFeature?.featureEntities?.length) {
            let filtered = dynamicFeature.featureEntities.filter((fe: any) => fe.renderMode !== 'inline' && fe.renderMode !== 'modal');
            
            if (showOnlyPlansAndCriteria !== undefined) {
                filtered = filtered.filter((fe: any) => !!fe.dynamicEntity?.isPlansAndCriteria === showOnlyPlansAndCriteria);
            }

            if (filtered.length > 0 && filtered[0].dynamicEntity) {
                setActiveDynamicTab(filtered[0].dynamicEntity.code);
            }
        }
    }, [dynamicFeature, activeDynamicTab, showOnlyPlansAndCriteria]);

    if (!dynamicFeature || !dynamicFeature.featureEntities?.length) return null;

    let inlineEntities = dynamicFeature.featureEntities.filter((e: any) => e.renderMode === 'inline');
    let tabEntities = dynamicFeature.featureEntities.filter((e: any) => e.renderMode !== 'inline' && e.renderMode !== 'modal');

    if (showOnlyPlansAndCriteria !== undefined) {
        inlineEntities = inlineEntities.filter((e: any) => !!e.dynamicEntity?.isPlansAndCriteria === showOnlyPlansAndCriteria);
        tabEntities = tabEntities.filter((e: any) => !!e.dynamicEntity?.isPlansAndCriteria === showOnlyPlansAndCriteria);
    }

    return (
        <div className="space-y-6">
            {/* 1. Entidades en modo 'inline' */}
            {inlineEntities.length > 0 && (
                <div className="space-y-6">
                    {inlineEntities.map((entity: any) => {
                        if (!entity.dynamicEntity) return null;
                        return (
                            <div key={entity.dynamicEntity.code} className="bg-transparent p-0">
                                <EntityContainer
                                    ref={(el: DynamicEntitiesRef | null) => { registerDynamicRef?.(entity.dynamicEntity!.code, el); }}
                                    featureCode={dynamicFeatureCode || ''}
                                    entityCode={entity.dynamicEntity.code}
                                    recordId={recordId ? String(recordId) : undefined}
                                    hideSubmitButton={true}
                                />
                            </div>
                        );
                    })}
                </div>
            )}

            {/* 2. Entidades en modo 'tab' */}
            {tabEntities.length > 0 && (
                <div className="bg-white dark:bg-dark-bg-primary border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                    <div className="flex border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-dark-bg-primary overflow-x-auto">
                        {tabEntities.map((entity: any) => {
                            if (!entity.dynamicEntity) return null;
                            return (
                                <button
                                    key={entity.dynamicEntity.code}
                                    type="button"
                                    onClick={() => setActiveDynamicTab(entity.dynamicEntity!.code)}
                                    className={`px-5 py-3 text-sm font-medium whitespace-nowrap transition-colors focus:outline-none ${activeDynamicTab === entity.dynamicEntity.code
                                        ? 'border-b-2 border-primary-600 text-primary-700 dark:text-primary-500'
                                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 border-b-2 border-transparent'
                                        }`}
                                >
                                    {entity.dynamicEntity.description || entity.dynamicEntity.code}
                                </button>
                            );
                        })}
                    </div>
                    <div className="p-5 bg-white dark:bg-dark-bg-primary min-h-[200px]">
                        {tabEntities.map((entity: any) => {
                            if (!entity.dynamicEntity) return null;
                            return (
                                <div
                                    key={entity.dynamicEntity.code}
                                    className={activeDynamicTab === entity.dynamicEntity.code ? 'block animate-in fade-in duration-300' : 'hidden'}
                                >
                                    <EntityContainer
                                        ref={(el: DynamicEntitiesRef | null) => { registerDynamicRef?.(entity.dynamicEntity!.code, el); }}
                                        featureCode={dynamicFeatureCode || ''}
                                        entityCode={entity.dynamicEntity.code}
                                        recordId={recordId ? String(recordId) : undefined}
                                        hideSubmitButton={true}
                                        hideTitle={true}
                                    />
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};
